import React, { useEffect, useState } from "react";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import { showError, showSuccess } from "../../utils/toastUtils";
import userAPI from "../../api/UserApi";
import { User, Mail, Phone, Check, ShoppingBag, MapPin } from "lucide-react";

function Account() {
  useAlphabeticInput();
  useNumericInput();

  const [errors, setErrors]   = useState({});
  const [saved,  setSaved]    = useState(false);
  const [formData, setFormData] = useState({
    phone:     "",
    firstName: "",
    lastName:  "",
    email:     "",
  });

  useEffect(() => {
    fetchMyDetails();
  }, []);

  const fetchMyDetails = async () => {
    const res = await userAPI.getDetails();
    if (res.code === 200) {
      const result = res.data;
      setFormData({
        phone:     result?.mobile    || "",
        firstName: result?.firstName || "",
        lastName:  result?.lastName  || "",
        email:     result?.email     || "",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "Please Enter First Name";
    if (!formData.phone)     newErrors.phone     = "Please Enter Phone Number";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const res = await userAPI.updateDetails(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setSaved(true);
        setTimeout(() => setSaved(false), 2600);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div>
      <p className="vp-eyebrow">Profile</p>
      <h2 className="vp-title">Account Details</h2>
      <div className="vp-rule" />

      <div className="row g-4 align-items-start">
        {/* ── Left: Form ── */}
        <div className="col-lg-7">
          <div className="vp-block">
            <p className="vp-block-title">Personal Information</p>

            <div className="row g-3">
              {/* First Name */}
              <div className="col-md-6">
                <label className="vp-label">First Name *</label>
                <div className="vp-icon-wrap">
                  <User size={13} />
                  <input
                    className="vp-input only-alphabets"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                  />
                </div>
                {errors.firstName && (
                  <p className="vp-error">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div className="col-md-6">
                <label className="vp-label">Last Name</label>
                <div className="vp-icon-wrap">
                  <User size={13} />
                  <input
                    className="vp-input only-alphabets"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              {/* Email — read-only */}
              <div className="col-12">
                <label className="vp-label">Email Address</label>
                <div className="vp-icon-wrap">
                  <Mail size={13} />
                  <input
                    className="vp-input vp-input-readonly"
                    type="email"
                    readOnly
                    name="email"
                    value={formData.email}
                  />
                </div>
                <p className="vp-note">Email address can't be changed.</p>
              </div>

              {/* Phone */}
              <div className="col-md-6">
                <label className="vp-label">Phone Number *</label>
                <div className="vp-icon-wrap">
                  <Phone size={13} />
                  <input
                    className="vp-input only-numeric"
                    type="text"
                    name="phone"
                    value={formData.phone}
                    maxLength={10}
                    onChange={handleChange}
                    placeholder="10-digit number"
                  />
                </div>
                {errors.phone && (
                  <p className="vp-error">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center mt-4">
              <button className="vp-btn-primary" onClick={handleDetailsSubmit}>
                Save Changes
              </button>
              {saved && (
                <span className="vp-success">
                  <Check size={13} /> Saved
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: Summary panel ── */}
        <div className="col-lg-4 offset-lg-1">
          <div className="vp-side-panel">
            <p className="vp-side-panel-title">Account Summary</p>
            {[
              { Icon: ShoppingBag, label: "Total Orders",   val: "—" },
              { Icon: MapPin,      label: "Saved Addresses", val: "—" },
            ].map(({ Icon, label, val }) => (
              <div key={label} className="vp-stat-row">
                <span className="vp-stat-row-label">
                  <Icon size={13} style={{ color: "var(--color-accent-main)" }} />
                  {label}
                </span>
                <span className="vp-stat-row-val">{val}</span>
              </div>
            ))}
          </div>

          <div className="vp-danger-panel">
            <p className="vp-danger-title">Danger Zone</p>
            <p className="vp-danger-text">
              Permanently remove your account and all associated data.
            </p>
            <button className="vp-danger-btn">Delete Account</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Account;